import { useState } from 'react';
import { api } from '@/lib/api-client';
import { Search, Send, HelpCircle, MessageSquare, CheckCircle } from 'lucide-react';

const faqs = [
  { q: 'How do I connect Telegram to my instance?', a: 'Go to Instance Details > Configuration and add your Telegram bot token.' },
  { q: 'How do I backup my instance?', a: 'Automated backups run daily. You can also create manual snapshots from Instance Details > Backups.' },
  { q: 'How do I install skills?', a: 'Visit the Skills Marketplace, find a skill, and click Install. It will be applied to your selected instance.' },
  { q: 'Can I downgrade my plan?', a: 'Yes, go to Billing > Current Plan and click Downgrade. Changes apply at your next billing cycle.' },
];

export default function Support() {
  const [search, setSearch] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const res = await api.submitTicket({ subject, message });
    setTicketId(res.ticketId);
    setSubmitted(true);
  };

  return (
    <div data-testid="support-page" className="space-y-6">
      <h1 className="text-2xl font-bold text-white font-mono">Support</h1>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input data-testid="support-search" type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search help articles..." className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm pl-10 pr-4 py-3 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" />
      </div>

      {/* FAQ */}
      <div>
        <h2 className="text-lg font-mono text-white font-bold mb-4">Frequently Asked</h2>
        <div className="space-y-2">
          {faqs.filter(f => !search || f.q.toLowerCase().includes(search.toLowerCase())).map((faq, i) => (
            <details key={i} data-testid={`support-faq-${i}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm group">
              <summary className="px-5 py-3 text-sm font-mono text-zinc-300 cursor-pointer hover:text-white flex items-center gap-2">
                <HelpCircle size={14} className="text-zinc-600 group-open:text-emerald-400" /> {faq.q}
              </summary>
              <p className="px-5 pb-3 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6">
        <h2 className="text-lg font-mono text-white font-bold mb-4 flex items-center gap-2"><MessageSquare size={18} className="text-emerald-400" /> Open a Ticket</h2>
        {submitted ? (
          <div data-testid="support-ticket-success" className="text-center py-6">
            <CheckCircle size={40} className="text-emerald-400 mx-auto mb-3" />
            <p className="text-white font-mono mb-1">Ticket Submitted</p>
            <p className="text-sm text-zinc-500 font-mono">Reference: {ticketId}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Subject</label>
              <input data-testid="support-subject" type="text" required value={subject} onChange={e => setSubject(e.target.value)} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600" placeholder="Brief description" />
            </div>
            <div>
              <label className="block text-xs font-mono text-zinc-500 mb-1.5">Message</label>
              <textarea data-testid="support-message" required value={message} onChange={e => setMessage(e.target.value)} rows={5} className="w-full bg-[#111] border border-zinc-800 text-white rounded-sm px-4 py-2.5 text-sm font-mono focus:border-emerald-500/50 outline-none placeholder-zinc-600 resize-none" placeholder="Describe your issue in detail..." />
            </div>
            <button data-testid="support-submit-btn" type="submit" className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-6 py-3 hover:bg-emerald-400 transition-all click-scale flex items-center gap-2">
              <Send size={14} /> Submit Ticket
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
